import React, { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import {
  Box,
  BoxProps,
  FormControl,
  Grid,
  Select
} from '@chakra-ui/react'

import { FormLabel } from '../components/FormLabel'
import { Switch } from '../components/Switch'
import {
  ExtraInsuranceCestovkoItemIconsEnum,
  getExtraInsuranceCestovkoIcon,
} from '../utils/getExtraInsuranceCestovkoIcon'
import { InputGroup } from '../components/InputGroup'
import { FormGroup } from '../components/FormGroup'
import { Step2FormValues } from './cestovko/Step2Form'
import { Datepicker } from '../components/Datepicker'
//import { useCountries } from '../hooks'
import { format, parse } from 'BrokerTsLib/src/Utils/formatInputs'
import { useInsuranceParamsContext } from '../layouts/Wizard'
import { InsuranceParamsTravelDto } from 'BrokerTsLib/src/dtos/Travel'
import { Insurer } from 'BrokerTsLib/src/enums'

const ExtraInsuranceItem = ({
  children,
  label,
  tooltip,
  icon,
  sx,
}: {
  children: React.ReactNode
  label: string
  tooltip?: string
  icon: ExtraInsuranceCestovkoItemIconsEnum | false
  sx?: BoxProps['sx']
}) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, ...sx }}>
      {icon && getExtraInsuranceCestovkoIcon(icon, '48px', 'secondary.500')}
      <FormControl sx={{}}>
        <FormLabel tooltip={tooltip}>{label}</FormLabel>
        {children}
      </FormControl>
    </Box>
  )
}

export const ExtraInsuranceCestovko = ({ showIcons = true }: { showIcons?: boolean }) => {
  const { register, watch, getValues, setValue, formState: { errors } } = useFormContext<Step2FormValues>()
  const { insuranceParams } = useInsuranceParamsContext()
  const isContracted = !!insuranceParams?.generalParams.contractNumber
  const insurers = (insuranceParams as InsuranceParamsTravelDto)?.baseParams?.insurers
  const firstRegistrationDateNeeded = insurers &&
    ((insurers.indexOf(Insurer.Colonnade) != -1) ||
      (insurers.indexOf(Insurer.Kooperativa) != -1))

  const allowVA = watch('addons.vehicleAssistanceAvailable')
  const allowCovid = watch('addons.covidInsuranceAvailable')
  const assistanceVehicle = watch('addons.vehicleAssistanceRequired')
  const stornoInsurance = watch('addons.stornoInsuranceRequired')

  const [holidayPriceString, setHolidayPriceString] = useState(format(getValues('addons.holidayPrice')?.toString()))
  const [holidayPricePerPersonString, setHolidayPricePerPersonString] = useState(format(getValues('addons.holidayPricePerPerson')?.toString()))

  const handleHolidayPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    const parsed = parse(value)
    const formatted = format(parsed)

    setHolidayPriceString(formatted)
    setValue('addons.holidayPrice', parseInt(parsed))
  }

  const handleHolidayPricePerPersonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    const parsed = parse(value)
    const formatted = format(parsed)

    setHolidayPricePerPersonString(formatted)
    setValue('addons.holidayPricePerPerson', parseInt(parsed))
  }

  const handleHolidayPriceOnBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    const parsed = parse(value)
    const formatted = format(parsed)

    if (!holidayPricePerPersonString && !Number.isNaN(parseInt(parsed))) {
      const insureesCount = (insuranceParams as InsuranceParamsTravelDto).travel.insurees?.length ?? 1;

      const valuePerPerson = Math.round(parseInt(parsed) / insureesCount);
      setValue('addons.holidayPricePerPerson', valuePerPerson)
      setHolidayPricePerPersonString(format(valuePerPerson.toString()))
    }
  }

  const handleVehicleRegMarkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value
    if (value.length > 0) {
      value = value.toUpperCase()
      const regex = /[^A-Z0-9]/gi
      value = value.replace(regex, '')
    }
    setValue('addons.vehicleRegistrationMark', value)
  }

  return (
    <Box sx={{ w: '100%', display: 'grid', gap: 4 }}>
      <Box
        sx={{
          w: '100%',
          display: 'grid',
          gridTemplateColumns: { base: '1fr', lg: 'auto auto 1fr' },
          gap: 8,
          placeItems: 'start',
        }}
      >
        {/* asistence vozidla */}
        {allowVA &&
          <Grid sx={{ gap: 8 }}>
            <ExtraInsuranceItem
              label="Asistence vozidla"
              tooltip="Pojištění asistenčních služeb vozidla na zvolenou cestu"
              icon={showIcons && ExtraInsuranceCestovkoItemIconsEnum.AssistanceVehicle}
            >
              <Switch {...register('addons.vehicleAssistanceRequired')} disabled={isContracted} />
            </ExtraInsuranceItem>

            {assistanceVehicle && (
              <>
                <FormControl isInvalid={!!errors.addons?.vehicleRegistrationMark} isRequired>
                  <FormLabel htmlFor="addons.vehicleRegistrationMark" tooltip='Zadejte SPZ pojišťovaného vozidla'>Reg. značka (SPZ)</FormLabel>

                  <InputGroup
                    id="addons.vehicleRegistrationMark"
                    placeholder="SPZ"
                    htmlSize={8}
                    isDisabled={isContracted}
                    autoComplete="off"
                    errors={errors.addons?.vehicleRegistrationMark}
                    {...register('addons.vehicleRegistrationMark', {
                      pattern: /[a-zA-Z]{6,8}/gi,
                      minLength: 6,
                      onChange: handleVehicleRegMarkChange
                    })}
                  />
                </FormControl>
                {firstRegistrationDateNeeded &&
                  <FormControl isInvalid={!!errors.addons?.vehicleFirstRegistrationDate} isRequired>
                    <FormLabel htmlFor="addons.vehicleFirstRegistrationDate">Datum první registrace</FormLabel>
                    <Datepicker
                      //isDisabled={isContracted}
                      errors={errors.addons?.vehicleFirstRegistrationDate}
                      date={getValues('addons.vehicleFirstRegistrationDate')}
                      onDateChange={(date) => setValue('addons.vehicleFirstRegistrationDate', date)}
                    />
                  </FormControl>
                }
              </>
            )}
          </Grid>
        }

        {/* covid */}
        {allowCovid &&
          <>
            <Grid sx={{ gap: 8 }}>
              <ExtraInsuranceItem
                label="COVID"
                tooltip="Pojištění Covid-19 proplatí náklady v případě onemocnění v zahraničí (léky, ošetření, hospitalizaci…)"
                icon={showIcons && ExtraInsuranceCestovkoItemIconsEnum.Covid}
              >
                <Switch {...register('addons.covidInsuranceRequired')} disabled={isContracted} />
              </ExtraInsuranceItem>

              {/* {covid &&
                <FormControl>
                  <FormLabel>Země</FormLabel>
                  <Select
                    value={covidId?.toString() ?? ''}
                    isDisabled={isContracted}
                    {...register('addons.covidInsuranceCountryId')}
                    onChange={(e) => setValue('addons.covidInsuranceCountryId', e.target.value === '' ? undefined : parseInt(e.target.value))}
                  >
                    <option value=''>
                      Vyberte zemi
                    </option>
                    {countries?.data &&
                      countries?.data.length > 0 &&
                      countries?.data.map((item) => (
                        <option key={item.id} value={item.id.toString()}>
                          {item.name}
                        </option>
                      ))}
                  </Select>
                </FormControl>
              } */}
            </Grid>
          </>
        }

        {/* pojisteni stornopoplatku */}
        <Grid sx={{ gap: 8 }}>
          <ExtraInsuranceItem
            label="Pojištění stornopoplatku"
            tooltip="Pojištění nákladů na stornopoplatky za objednanané služby"
            icon={showIcons && ExtraInsuranceCestovkoItemIconsEnum.CancellationInsurance}
          >
            <Switch {...register('addons.stornoInsuranceRequired')} disabled={isContracted} />
          </ExtraInsuranceItem>

          {stornoInsurance && (
            <FormGroup>
              <FormControl isInvalid={!!errors.addons?.holidayPrice} isRequired>
                <FormLabel htmlFor={`addons.holidayPrice`}>Celková cena zájezdu</FormLabel>

                <InputGroup
                  id={`addons.holidayPrice`}
                  placeholder="Např. 50 000"
                  type="string"
                  htmlSize={12}
                  isDisabled={isContracted}
                  // min="0"
                  autoComplete="off"
                  // inputMode="decimal"
                  rightElement="Kč"
                  errors={errors.addons?.holidayPrice}
                  value={holidayPriceString}
                  onChange={handleHolidayPriceChange}
                  onBlur={handleHolidayPriceOnBlur}
                />
              </FormControl>

              <FormControl isInvalid={!!errors.addons?.holidayPricePerPerson} isRequired>
                <FormLabel htmlFor={`addons.holidayPricePerPerson`}>Cena zájezdu za 1 osobu</FormLabel>

                <InputGroup
                  id={`addons.holidayPricePerPerson`}
                  placeholder="Např. 20 000"
                  type="string"
                  htmlSize={12}
                  isDisabled={isContracted}
                  // min="0"
                  autoComplete="off"
                  // inputMode="decimal"
                  rightElement="Kč"
                  errors={errors.addons?.holidayPricePerPerson}
                  value={holidayPricePerPersonString}
                  onChange={handleHolidayPricePerPersonChange}
                />
              </FormControl>


              <FormControl isInvalid={!!errors.addons?.holidayPurchaseDate} isRequired>
                <FormLabel htmlFor="addons.holidayPurchaseDate">Datum zakoupení zájezdu</FormLabel>
                <Datepicker
                  isDisabled={isContracted}
                  errors={errors.addons?.holidayPurchaseDate}
                  date={getValues('addons.holidayPurchaseDate')}
                  onDateChange={(date) => setValue('addons.holidayPurchaseDate', date)}
                />
              </FormControl>
            </FormGroup>
          )}
        </Grid>
      </Box>
    </Box >
  )
}
